import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, Info, X } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import type { WebsiteCategoryId } from "@/lib/rules/websiteCategories";
import { WEBSITE_CATEGORIES } from "@/lib/rules/websiteCategories";

type WebsiteCategoryPickerModalProps = {
  open: boolean;
  selected: WebsiteCategoryId[];
  onChange: (next: WebsiteCategoryId[]) => void;
  onClose: () => void;
  /** When false, toggling on an unselected category is blocked (VPN prerequisite). */
  canAddCategories?: boolean;
  onAddBlocked?: () => void;
};

export function WebsiteCategoryPickerModal({
  open,
  selected,
  onChange,
  onClose,
  canAddCategories = true,
  onAddBlocked,
}: WebsiteCategoryPickerModalProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const copy = t.websiteCategories;
  const prefersReducedMotion = useReducedMotion() ?? false;
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [expandedId, setExpandedId] = useState<WebsiteCategoryId | null>(null);

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setExpandedId(null);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const root = document.getElementById("website-category-picker-modal");
      if (!root) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  const toggleCategory = (id: WebsiteCategoryId) => {
    const isSelected = selected.includes(id);
    if (!isSelected && !canAddCategories) {
      onAddBlocked?.();
      return;
    }
    const next = isSelected ? selected.filter((item) => item !== id) : [...selected, id];
    onChange(next);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          id="website-category-picker-modal"
          className="fixed inset-0 z-[70] flex flex-col bg-[oklch(10%_0.012_160)]"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        >
          <header
            className="flex shrink-0 items-center justify-between gap-3 px-4 pb-2 pt-[max(16px,env(safe-area-inset-top))]"
          >
            <h2 id={titleId} className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
              {copy.modalTitle}
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={close}
              className="icon-button"
              style={{ minWidth: 44, minHeight: 44, border: "none" }}
              aria-label={t.common.close}
            >
              <X size={18} />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 [-webkit-overflow-scrolling:touch]">
            <div className="grid gap-2">
              {WEBSITE_CATEGORIES.map((category) => {
                const checked = selected.includes(category.id);
                const expanded = expandedId === category.id;
                const label = copy[category.id].name;
                return (
                  <div key={category.id} className="liquid-glass-inset overflow-hidden rounded-xl">
                    <div className="flex items-center gap-2 p-2.5">
                      <button
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        aria-pressed={checked}
                        aria-label={formatTemplate(copy.toggleCategoryAria, { name: label })}
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[oklch(88%_0.02_150)]">
                          {label}
                        </span>
                        <span
                          className={`grid h-6 w-10 shrink-0 place-items-center rounded-full transition-colors ${
                            checked
                              ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]"
                              : "liquid-glass-day-inactive text-transparent"
                          }`}
                          aria-hidden
                        >
                          <Check size={14} />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : category.id)}
                        className="liquid-glass-icon-ring grid h-8 w-8 shrink-0 place-items-center text-[oklch(72%_0.09_155)]"
                        aria-expanded={expanded}
                        aria-label={
                          expanded
                            ? formatTemplate(copy.collapseInfoAria, { name: label })
                            : formatTemplate(copy.expandInfoAria, { name: label })
                        }
                      >
                        <Info size={15} aria-hidden />
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {expanded ? (
                        <motion.div
                          initial={prefersReducedMotion ? false : { height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
                          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
                          className="overflow-hidden"
                        >
                          <div className="border-t border-[oklch(100%_0_0/0.06)] px-3 pb-3 pt-2">
                            <p className="pb-2 text-xs leading-5 text-[oklch(68%_0.025_155)]">
                              {copy[category.id].description}
                            </p>
                            <p className="pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
                              {copy.domainsHeading}
                            </p>
                            <p className="max-h-28 overflow-y-auto text-xs leading-5 text-[oklch(76%_0.025_155)] [-webkit-overflow-scrolling:touch]">
                              {category.domains.join(", ")}
                            </p>
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="liquid-glass-sheet-footer shrink-0 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
            <button type="button" onClick={close} className="primary-action w-full">
              {t.common.done}
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

export function websiteCategoryLabel(
  id: WebsiteCategoryId,
  t: ReturnType<typeof getTranslations>,
): string {
  return t.websiteCategories[id].name;
}
